import { useCallback, useMemo } from 'react'
import { v4 as uuid } from 'uuid'
import dayjs from 'dayjs'
import { type LinkMarkSuggestionItem } from '@packages/blocknote-core'
import { useCreateNote } from '../../hooks/useCreateNote'
import {
  type SelectedDate,
  dateToWeek,
  selectedDateToKey,
  useSelectedDate,
  useSelectedDateDispatch,
} from '../../contexts/SelectedDate'
import useNote, { type useNoteConfig } from '../../hooks/useNote'
import {
  NoteType,
  filenameToKey,
  isCalendarNote,
  isTeamspaceNote,
} from '../../utils/note'
import { tagList } from '../../utils/tagList'
import { noteList } from '../../utils/noteList'
import { dateLinkSuggestions } from '../../utils/dateLinkSuggestionParser'
import { useSidebarProvider } from '../../contexts/Sidebar'
import { useSelectedRecordName } from '../../contexts/SelectedRecordName'
import {
  usePrivateProjectNotesByTitle,
  useTeamProjectNotesByTitle,
} from '../../hooks/useNotesFactory'
import { TipTapEditor } from './TipTapEditor'
import { WeekNoteReference } from './WeekNoteReference'
import { NoteReference } from './NoteReference'
import { NoteHeader } from './NoteHeader'
import { FromNote, useReferences } from '../../providers/ReferencesContext'
import { TrialBanner } from '../payments/TrialBanner'
import { useSubscriptionVerification } from '../../contexts/SubscriptionVerification'

type Props = {
  showCalendar: boolean
  toggleCalendar: () => void
  onCommandBarOpen: (search: string) => void
}

export default function NoteEditor({
  showCalendar,
  toggleCalendar,
  onCommandBarOpen,
}: Props) {
  const selectedRecordName = useSelectedRecordName()
  const {
    noteKey,
    handleSelectRecordName,
    handleRevealNote,
    shouldForceUpdateEditor,
    notes: notesMap,
  } = useSidebarProvider()
  const { isLoading, data: note } = useNote(noteKey)
  const selectedDate = useSelectedDate()
  const selectedDay: SelectedDate = useMemo(
    () => ({
      activePeriod: 'day',
      date: selectedDate.date,
      ...dateToWeek(selectedDate.date),
    }),
    [selectedDate]
  )
  const weekNoteKey: useNoteConfig | undefined = useMemo(() => {
    if (
      isCalendarNote(noteKey.noteType) &&
      selectedDate.activePeriod === 'day'
    ) {
      // because selectedDate can store a different day and week, we need always the selected day for the key
      const key = selectedDateToKey({ ...selectedDay, activePeriod: 'week' })
      return { ...noteKey, recordName: 'weekly', filename: key, key }
    }
    return
  }, [noteKey, selectedDate.activePeriod, selectedDay])
  const { isLoading: isLoadingWeek, data: weekNote } = useNote(weekNoteKey)

  //#region references
  const references = useReferences()
  const filteredNoteReferences = useMemo(() => {
    if (!note) return new Map<string, FromNote>()
    const noteID = isCalendarNote(note.noteType)
      ? filenameToKey(note.filename)
      : note.recordName ?? selectedRecordName
    if (!noteID) return new Map<string, FromNote>()

    return references.get(noteID) ?? new Map<string, FromNote>()
  }, [references, note, selectedRecordName])
  //#endregion

  // # region mark
  const { data: privateNotesByTitle } = usePrivateProjectNotesByTitle()
  const { data: teamNotesByTitle } = useTeamProjectNotesByTitle()
  const createNoteMutation = useCreateNote()
  const selectedDateDispatch = useSelectedDateDispatch()

  // When a user clicks on a mark such as a hashtag, wikilink, datelink...
  const handleMarkClicked = useCallback(
    (event: MouseEvent) => {
      const element = event.target as HTMLElement
      const textContent = element.textContent
      if (!textContent) return

      if (Object.hasOwn(element.dataset, 'hashtag')) {
        onCommandBarOpen(`tag: ${textContent}`)
      } else if (Object.hasOwn(element.dataset, 'wikilink')) {
        const wikiLink = textContent
        const notes = isTeamspaceNote(noteKey.noteType)
          ? teamNotesByTitle?.get(wikiLink)
          : privateNotesByTitle?.get(wikiLink)
        const altNotes = isTeamspaceNote(noteKey.noteType)
          ? privateNotesByTitle?.get(wikiLink)
          : teamNotesByTitle?.get(wikiLink)
        // attempt to open the 'closest' note first
        const note =
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          (notes && notes.length > 0 && notes[0]) ||
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          (altNotes && altNotes.length > 0 && altNotes[0]) ||
          undefined

        if (note?.recordName) {
          handleRevealNote(note.recordName)
        } else {
          const recordName = uuid()
          createNoteMutation.mutate(
            {
              content: `# ${wikiLink}`,
              filename: wikiLink,
              parent: noteKey.parent,
              recordName,
              noteType: isTeamspaceNote(noteKey.noteType)
                ? NoteType.TEAM_SPACE_NOTE
                : NoteType.PROJECT_NOTE,
              isDir: false,
            },
            {
              onSuccess: () => {
                handleRevealNote(recordName)
              },
            }
          )
        }
      } else if (Object.hasOwn(element.dataset, 'datelink')) {
        if (textContent === '>today') {
          selectedDateDispatch({ type: 'today', forceDay: true })
          handleSelectRecordName('daily')
        } else {
          // if (dateLink === '@today') {
          //   selectedDateDispatch({ type: 'today', forceDay: true })
          // } else if (dateLink === '@tomorrow') {
          //   selectedDateDispatch({ type: 'add', amount: 1, unit: 'day' })
          // } else if (dateLink === '@yesterday') {
          //   selectedDateDispatch({ type: 'subtract', amount: 1, unit: 'day' })
          // } else {
          const weekRegex = />(\d{4})-W(\d{1,2})/
          const matches = weekRegex.exec(textContent)

          if (matches) {
            const year = Number.parseInt(matches[1])
            const week = Number.parseInt(matches[2])

            selectedDateDispatch({ type: 'setWeek', year, week })
            handleSelectRecordName('weekly')
          } else {
            const cleanedDateLink = textContent.replaceAll(/[<>@]/g, '')
            const date = dayjs(cleanedDateLink)

            if (date.isValid()) {
              selectedDateDispatch({ type: 'setDay', date })
              handleSelectRecordName('daily')
            }
          }
        }
      }
    },
    [
      onCommandBarOpen,
      noteKey.noteType,
      noteKey.parent,
      teamNotesByTitle,
      privateNotesByTitle,
      handleRevealNote,
      createNoteMutation,
      selectedDateDispatch,
    ]
  )

  const handleLoadSuggestions = useCallback(
    (prefix: string, keyword: string): (string | LinkMarkSuggestionItem)[] => {
      // Implemented logic to load hashtags
      const notes = [...notesMap.values()].filter(
        (note) => !note.isFolder && !note.filename.startsWith('@')
      )

      if (['@', '#'].includes(prefix)) {
        return tagList(notes, prefix)
      }

      // WikiLinks auto-completion
      if (prefix === '[[') {
        return noteList(notes)
      }

      if (prefix === '>') {
        return dateLinkSuggestions(keyword)
      }

      return []
    },
    [notesMap]
  )
  // # endregion

  const {
    isTrialLoading,
    setDialogOpen: setPaywallOpen,
    trialEndDate,
    isSubscribed,
    isGuest,
    isTrialRunning,
    trackTrialBannerClicked,
  } = useSubscriptionVerification()

  const isValidUserStatus =
    isTrialLoading || isGuest || isSubscribed || isTrialRunning === 'true'
  const isTodaySelected =
    selectedDate.date.isSame(new Date(), 'day') &&
    selectedRecordName === 'daily'

  const isEditable = Boolean(isValidUserStatus || isTodaySelected)

  return (
    <div className='note-body flex flex-col'>
      {!isSubscribed && !isTrialLoading && (
        <TrialBanner
          isTrialRunning={isTrialRunning}
          trialEndDate={trialEndDate ?? ''}
          isGuest={isGuest}
          onClick={() => {
            trackTrialBannerClicked()
            setPaywallOpen(true)
          }}
        />
      )}
      <NoteHeader
        showCalendar={showCalendar}
        toggleCalendar={toggleCalendar}
        noteType={noteKey.noteType}
      />
      <div className='editor-container-wrapper'>
        {Boolean(weekNoteKey) && (
          <WeekNoteReference
            isLoading={isLoadingWeek}
            weekNote={weekNote}
            selectedDate={selectedDay}
            jumpToWeek={({ week, year }) => {
              selectedDateDispatch({ type: 'setWeek', week, year })
            }}
          />
        )}
        {filteredNoteReferences.size > 0 && (
          <NoteReference
            references={filteredNoteReferences}
            // onSelectNote={handleRevealNote}
            onSelectNote={handleSelectRecordName}
          />
        )}
        {(Boolean(weekNoteKey) || filteredNoteReferences.size > 0) && (
          <div className='blocknote-padding mx-auto mt-4 w-full max-w-3xl'>
            <hr className='my-1 w-full opacity-25' />
          </div>
        )}
        <TipTapEditor
          shouldForceUpdateEditor={shouldForceUpdateEditor}
          isLoading={isLoading}
          note={note}
          onMarkClicked={handleMarkClicked}
          onLoadSuggestions={handleLoadSuggestions}
          isEditable={isEditable}
          handleClick={() => {
            if (!isEditable) {
              setPaywallOpen(true)
            }
          }}
        />
      </div>
    </div>
  )
}
